// ONDA — mobile-only product-page redesign ("stile Zara").
// type="module" so it can reuse the theme's own event contract instead of
// reinventing one, and so execution is naturally deferred until the DOM
// (including everything below this tag) has been parsed.
import { StandardEvents } from '@shopify/events';

const SCOPE_SELECTOR = '.product-details';
const GALLERY_SELECTOR = '.custom-mobile-gallery';
const BAR_ID = 'CustomMobileBar';

/**
 * Custom elements are the only tags guaranteed to contain a hyphen. Cloning
 * one would re-trigger its connectedCallback/behaviour inside the bar, so we
 * swap it for a plain <div> that keeps the same attributes (and therefore the
 * same CSS classes) but does nothing on its own.
 * @param {Element} root
 */
function stripCustomElements(root) {
  const customEls = Array.from(root.querySelectorAll('*')).filter((el) => el.tagName.includes('-'));
  for (const el of customEls) {
    const div = document.createElement('div');
    for (const attr of Array.from(el.attributes)) {
      div.setAttribute(attr.name, attr.value);
    }
    while (el.firstChild) div.appendChild(el.firstChild);
    el.replaceWith(div);
  }
}

/**
 * Removes id/for/name/form everywhere in the clone so it can never collide
 * with (or accidentally submit through) the original elements.
 * @param {Element} root
 */
function stripIdentityAttributes(root) {
  const all = [root, ...root.querySelectorAll('[id],[for],[name],[form]')];
  for (const el of all) {
    el.removeAttribute('id');
    el.removeAttribute('for');
    el.removeAttribute('name');
    el.removeAttribute('form');
  }
}

/**
 * Theme blocks (price, title, variant options...) carry an inline `--width`
 * custom property (and sometimes a literal `width`) sized for their original
 * full-column layout — e.g. price renders with `--width: 100%`, which claims
 * the whole flex row and collapses the title next to it to 0. None of that
 * sizing means anything in the compact bar, so it's stripped everywhere.
 * @param {Element} root
 */
function stripInlineSizing(root) {
  const all = [root, ...root.querySelectorAll('[style]')];
  for (const el of all) {
    if (!(el instanceof HTMLElement) || !el.style) continue;
    el.style.removeProperty('width');
    el.style.removeProperty('--width');
    el.style.removeProperty('max-width');
  }
}

/**
 * @param {Element} node
 * @returns {Element}
 */
function sanitizedClone(node) {
  const clone = /** @type {Element} */ (node.cloneNode(true));
  stripCustomElements(clone);
  stripIdentityAttributes(clone);
  stripInlineSizing(clone);
  return clone;
}

/**
 * Wires one cloned radio: clicking it selects the matching original input
 * (matched by the stable data-option-value-id) and fires a real `change` on
 * it, which is exactly what variant-picker.js listens for.
 * @param {HTMLElement} cloneFieldset
 * @param {HTMLInputElement} cloneInput
 * @param {Element} originalFieldset
 */
function wireOption(cloneFieldset, cloneInput, originalFieldset) {
  cloneInput.addEventListener('click', (event) => {
    event.preventDefault();
    const valueId = cloneInput.dataset.optionValueId;
    if (!valueId) return;
    const original = /** @type {HTMLInputElement | null} */ (
      originalFieldset.querySelector(`input[data-option-value-id="${CSS.escape(valueId)}"]`)
    );
    if (!original || original.disabled) return;

    // Optimistic visual feedback inside the clone itself.
    cloneFieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
    });
    cloneInput.checked = true;

    original.checked = true;
    original.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**
 * Rebuilds the bottom bar from the CURRENT (already up to date) live DOM of
 * the product details block. Called on init and again once Horizon has
 * finished morphing everything after a variant change.
 */
function rebuildBar() {
  const bar = document.getElementById(BAR_ID);
  const scope = document.querySelector(SCOPE_SELECTOR);
  if (!bar || !scope) return;

  bar.innerHTML = '';

  const topRow = document.createElement('div');
  topRow.className = 'custom-mobile-bar__row custom-mobile-bar__row--top';

  const titleSource = scope.querySelector('h1');
  if (titleSource) {
    const title = sanitizedClone(titleSource);
    title.classList.add('custom-mobile-bar__title');
    topRow.appendChild(title);
  }

  const priceSource = scope.querySelector('product-price');
  if (priceSource) {
    const price = sanitizedClone(priceSource);
    price.classList.add('custom-mobile-bar__price');
    topRow.appendChild(price);
  }

  if (topRow.childNodes.length) bar.appendChild(topRow);

  const pickerSource = scope.querySelector('variant-picker');
  if (pickerSource) {
    const originalFieldsets = pickerSource.querySelectorAll('fieldset.variant-option');
    originalFieldsets.forEach((originalFieldset) => {
      const clone = /** @type {HTMLElement} */ (sanitizedClone(originalFieldset));
      bar.appendChild(clone);

      const cloneInputs = clone.querySelectorAll('input[type="radio"]');
      cloneInputs.forEach((cloneInput) => {
        wireOption(clone, /** @type {HTMLInputElement} */ (cloneInput), originalFieldset);
      });
    });
  }

  const buttonRow = document.createElement('div');
  buttonRow.className = 'custom-mobile-bar__row custom-mobile-bar__row--button';

  const addToCartSource = scope.querySelector('.add-to-cart-button');
  if (addToCartSource) {
    const button = /** @type {HTMLButtonElement} */ (sanitizedClone(addToCartSource));
    button.classList.add('custom-mobile-bar__add-to-cart');
    button.addEventListener('click', (event) => {
      event.preventDefault();
      /** @type {HTMLButtonElement} */ (addToCartSource).click();
    });
    buttonRow.appendChild(button);
  }

  if (buttonRow.childNodes.length) bar.appendChild(buttonRow);
}

/**
 * Filters the mobile gallery to the given variant's image group without a
 * fetch, then restores the reading position so the page never appears to
 * scroll on its own.
 * @param {string | null} groupTag
 */
function filterGallery(groupTag) {
  const gallery = document.querySelector(GALLERY_SELECTOR);
  if (!gallery) return;

  // Anchor on the details block, which this function never touches: keeping
  // ITS page-absolute position constant is a direction-agnostic way to
  // compensate for any height change in the gallery above it, whether the
  // reader is mid-gallery or already scrolled past it.
  const anchor = document.querySelector(SCOPE_SELECTOR);
  const beforeAnchorTop = anchor ? anchor.getBoundingClientRect().top + window.scrollY : null;

  const figures = gallery.querySelectorAll('[data-media-id]');
  figures.forEach((figure) => {
    const tag = figure.dataset.group;
    const visible = tag === 'g' || (groupTag != null && tag === groupTag);
    /** @type {HTMLElement} */ (figure).hidden = !visible;
  });
  gallery.dataset.selectedGroup = groupTag ?? 'g';

  if (anchor && beforeAnchorTop != null) {
    const afterAnchorTop = anchor.getBoundingClientRect().top + window.scrollY;
    const delta = afterAnchorTop - beforeAnchorTop;
    if (delta !== 0) window.scrollBy(0, delta);
  }
}

/**
 * Keeps the fixed bar hidden while the real product-details block is on
 * screen, visible everywhere else (over the photos, and again once the
 * reader scrolls past the details into description/FAQ/reviews).
 */
function initVisibility() {
  const bar = document.getElementById(BAR_ID);
  const scope = document.querySelector(SCOPE_SELECTOR);
  if (!bar || !scope) return;

  const setBarHeightVar = () => {
    document.documentElement.style.setProperty('--teto-bar-h', `${bar.offsetHeight}px`);
  };

  const show = () => {
    bar.classList.add('is-visible');
    setBarHeightVar();
    document.body.classList.add('has-custom-mobile-bar');
  };

  const hide = () => {
    bar.classList.remove('is-visible');
    document.body.classList.remove('has-custom-mobile-bar');
  };

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) hide();
      else show();
    },
    { threshold: 0 }
  );
  observer.observe(scope);

  const resizeObserver = new ResizeObserver(() => {
    if (bar.classList.contains('is-visible')) setBarHeightVar();
  });
  resizeObserver.observe(bar);
}

function init() {
  // No width guard needed: above 999px the CSS keeps .custom-mobile-gallery
  // and .custom-mobile-bar hidden, so this logic is simply inert on desktop.
  rebuildBar();
  initVisibility();

  document.addEventListener(StandardEvents.productSelect, (event) => {
    if (!(event.target instanceof Element)) return;

    event.promise
      .then(({ detail }) => {
        const featuredImageId = detail?.resource?.featured_image?.id;
        filterGallery(featuredImageId != null ? String(featuredImageId) : null);
        rebuildBar();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[custom-mobile-pdp] Event promise rejected:', error);
      });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
